import type { AppLocale, MessageTree } from "@airange/shared/i18n-common";

export const redblueSchemeIntelligencePageMessages: Record<AppLocale, MessageTree> = {
  "zh-CN": {
    redblue: {
      pages: {
        schemeDetail: {
          intelligence: {
            types: {
              attackChain: "攻击链推演",
              phishingSimulation: "受控钓鱼演练",
            },
            defaults: {
              attackChainIntent:
                "请生成一条面向政企内网攻防演练的多阶段受控推演设计：以边界暴露面风险为初始线索，串联初始访问模拟、身份凭据风险发现、横向移动迹象识别、蓝方检测处置和复盘评分。所有攻击动作仅作为人工审核的演练任务和安全占位，不输出真实漏洞利用载荷、恶意脚本、C2、远控、凭据采集或检测绕过内容，并要求给出阶段、任务、检测点、任务评分配置、报告模板和知识引用。",
              phishingIntent:
                "请生成一条面向政企员工安全意识训练的受控智能钓鱼专项演练设计：围绕授权模拟邮箱或隔离账号，生成可审核的邮件主题、发件人身份、正文、行动引导、风险提示、检测指标、蓝方处置任务、任务评分配置和复盘报告。内容仅用于安全教育和隔离演练，不输出真实凭据收集、恶意附件、绕过检测、外部投递或社会工程攻击执行指令。",
            },
            attackChainClasses: {
              social_engineering_identity_response: "社会工程与身份响应",
              exposure_initial_access_boundary_response: "暴露面与边界响应",
              credential_lateral_movement_segmentation: "凭据风险与横向阻断",
              cloud_native_workload_response: "云原生工作负载响应",
              persistence_honeypot_attribution: "驻留线索、蜜罐与溯源",
            },
            statuses: {
              pending: "等待中",
              running: "生成中",
              generated: "待审核",
              applied: "已采纳",
              rejected: "已驳回",
              failed: "生成失败",
            },
            languageOptions: {
              zhCN: "简体中文",
              enUS: "English",
              jaJP: "日本語",
            },
            actions: {
              generate: "生成推演设计",
              refresh: "刷新",
              retry: "重试",
              view: "查看详情",
              apply: "采纳到当前版本",
              reject: "驳回",
            },
            sections: {
              generate: "智能生成",
              history: "生成与审核记录",
              designSummary: "设计摘要",
              stages: "推演阶段",
              tasks: "演练任务",
              reports: "报告模板",
              knowledge: "知识引用",
              phishingArtifact: "受控钓鱼工件",
              rawOutput: "失败原始输出",
            },
            fields: {
              id: "记录 ID",
              type: "生成类型",
              attackChainClass: "攻击链分类",
              intent: "推演意图",
              scenario: "钓鱼场景",
              language: "内容语言",
              latency: "耗时",
              reviewNote: "审核意见",
              stage: "阶段",
              task: "任务",
              scoreKind: "评分方式",
              maxScore: "最高分",
              reference: "引用标识",
              rationale: "引用理由",
              generationType: "生成类型",
              requestId: "智能中心 request_id",
              traceId: "链路 trace_id",
              provider: "提供方",
              model: "模型",
              promptVersion: "提示词版本",
              reviewedBy: "审核人",
              reviewedAt: "审核时间",
              errorCode: "错误码",
              errorMessage: "错误信息",
              summary: "摘要",
              safety: "安全分级",
              targetRole: "目标角色",
              sender: "模拟发件人",
              subject: "邮件主题",
              callToAction: "模拟行动提示",
              body: "邮件正文",
              detectionIndicators: "识别线索",
            },
            placeholders: {
              attackChainClass: "选择本次生成对应的验收分类",
              intent: "描述目标环境、训练目标、预期阶段与约束条件",
              scenario: "选择一种受控演练场景",
              reviewNote: "记录采纳或驳回依据",
            },
            validation: {
              attackChainClassRequired: "攻击链推演必须选择分类。",
              intentRequired: "请输入推演意图。",
              scenarioRequired: "受控钓鱼演练必须选择场景。",
            },
            notices: {
              providerTitle: "由智能中心生成，RedBlue 审核采纳",
              providerDescription:
                "系统不会使用本地模板伪造成功。攻击链和钓鱼内容仅用于隔离靶场内的受控演练，钓鱼地址固定使用 .invalid 保留域，不提供外部投递、凭据收集或恶意载荷。",
              emptyHistory: "当前版本还没有智能生成记录。",
            },
            messages: {
              loadFailed: "加载智能生成记录失败",
              generated: "智能中心已返回待审核设计",
              generateFailed: "智能生成失败",
              applied: "已采纳到当前草稿版本",
              applyFailed: "采纳失败",
              rejected: "已驳回该生成记录",
              rejectFailed: "驳回失败",
              deleted: "已删除生成记录",
              deleteFailed: "删除生成记录失败",
              contractMissing:
                "成功状态缺少必填设计结果，记录不能审核或采纳。请依据 request_id、trace_id 和原始响应排查服务端契约。",
            },
            confirm: {
              applyTitle: "采纳这份智能设计？",
              applyDescription:
                "当前草稿版本的阶段、任务（含评分配置）和报告模板将被这份设计替换，并保留生成与审核快照。",
              rejectTitle: "驳回这份智能设计？",
              deleteTitle: "删除这条生成记录？",
              deleteDescription: "仅会清理这条未采纳的生成历史，不会修改当前方案版本或已采纳设计。",
            },
            scenarios: {
              government_notice: "政务通知",
              meeting_invitation: "会议邀请",
              financial_approval: "财务审批",
              supply_chain_collaboration: "供应链协作",
              recruitment: "招聘沟通",
              cloud_drive_share: "云盘分享",
              password_expiry: "密码到期",
              system_upgrade: "系统升级",
              customer_complaint: "客户投诉",
              delivery_notice: "物流通知",
              project_delivery: "项目交付",
              urgent_security_notice: "紧急安全通知",
            },
            empty: "暂无",
          },
        },
      },
    },
  },
  en: {
    redblue: {
      pages: {
        schemeDetail: {
          intelligence: {
            types: {
              attackChain: "Attack Chain",
              phishingSimulation: "Controlled Phishing",
            },
            defaults: {
              attackChainIntent:
                "Generate a multi-stage controlled exercise design for government and enterprise internal-network attack-defense training: start from an exposed-boundary risk signal, connect initial-access simulation, credential-risk discovery, lateral-movement indicator identification, blue-team detection and response, and after-action scoring. All attack actions are only reviewed exercise tasks and safety placeholders. Do not output real exploit payloads, malicious scripts, C2, remote control, credential collection, or detection-bypass content, and include stages, tasks, detection points, task scoring config, report templates, and knowledge references.",
              phishingIntent:
                "Generate a controlled intelligent phishing exercise design for government and enterprise employee security-awareness training: use authorized simulated mailboxes or isolated accounts, and provide reviewable email subjects, sender identity, body copy, call to action, risk prompts, detection metrics, blue-team response tasks, task scoring config, and after-action reports. This content is only for security education and isolated exercises. Do not output real credential collection, malicious attachments, detection bypass, external delivery, or social-engineering execution instructions.",
            },
            attackChainClasses: {
              social_engineering_identity_response: "Social Engineering and Identity Response",
              exposure_initial_access_boundary_response: "Exposure and Boundary Response",
              credential_lateral_movement_segmentation:
                "Credential Risk and Lateral Movement Containment",
              cloud_native_workload_response: "Cloud-native Workload Response",
              persistence_honeypot_attribution: "Persistence, Honeypot, and Attribution",
            },
            statuses: {
              pending: "Pending",
              running: "Running",
              generated: "Review Required",
              applied: "Applied",
              rejected: "Rejected",
              failed: "Failed",
            },
            languageOptions: {
              zhCN: "简体中文",
              enUS: "English",
              jaJP: "日本語",
            },
            actions: {
              generate: "Generate Design",
              refresh: "Refresh",
              retry: "Retry",
              view: "View Details",
              apply: "Apply to Current Version",
              reject: "Reject",
            },
            sections: {
              generate: "Intelligent Generation",
              history: "Generation and Review History",
              designSummary: "Design Summary",
              stages: "Exercise Stages",
              tasks: "Exercise Tasks",
              reports: "Report Templates",
              knowledge: "Knowledge References",
              phishingArtifact: "Controlled Phishing Artifact",
              rawOutput: "Raw Failed Output",
            },
            fields: {
              id: "Record ID",
              type: "Generation Type",
              attackChainClass: "Attack-chain Class",
              intent: "Exercise Intent",
              scenario: "Phishing Scenario",
              language: "Language",
              latency: "Latency",
              reviewNote: "Review Note",
              stage: "Stage",
              task: "Task",
              scoreKind: "Score Kind",
              maxScore: "Max Score",
              reference: "Reference",
              rationale: "Rationale",
              generationType: "Generation Type",
              requestId: "Intelligence request_id",
              traceId: "Trace trace_id",
              provider: "Provider",
              model: "Model",
              promptVersion: "Prompt Version",
              reviewedBy: "Reviewed By",
              reviewedAt: "Reviewed At",
              errorCode: "Error Code",
              errorMessage: "Error Message",
              summary: "Summary",
              safety: "Safety Class",
              targetRole: "Target Role",
              sender: "Simulated Sender",
              subject: "Subject",
              callToAction: "Simulated Call to Action",
              body: "Body",
              detectionIndicators: "Detection Indicators",
            },
            placeholders: {
              attackChainClass: "Select the acceptance class for this generation",
              intent:
                "Describe the target environment, training objective, expected stages, and constraints",
              scenario: "Select a controlled exercise scenario",
              reviewNote: "Record the reason for applying or rejecting this design",
            },
            validation: {
              attackChainClassRequired: "Attack-chain generation requires a class.",
              intentRequired: "Enter the exercise intent.",
              scenarioRequired: "Controlled phishing requires a scenario.",
            },
            notices: {
              providerTitle: "Generated by Intelligence Center and reviewed in RedBlue",
              providerDescription:
                "No local template can fabricate a successful result. Attack-chain and phishing content is restricted to controlled exercises in an isolated range. Phishing addresses use the reserved .invalid domain, with no external delivery, credential capture, or malicious payloads.",
              emptyHistory: "No intelligent generation records exist for this version.",
            },
            messages: {
              loadFailed: "Failed to load intelligent generation records",
              generated: "Intelligence Center returned a design for review",
              generateFailed: "Intelligent generation failed",
              applied: "Applied to the current draft version",
              applyFailed: "Failed to apply design",
              rejected: "Generation rejected",
              rejectFailed: "Failed to reject generation",
              deleted: "Generation record deleted",
              deleteFailed: "Failed to delete generation record",
              contractMissing:
                "A successful status is missing the required design result. Review and apply are blocked. Diagnose the server contract using request_id, trace_id, and the raw response.",
            },
            confirm: {
              applyTitle: "Apply this intelligent design?",
              applyDescription:
                "The stages, tasks including scoring config, and report templates in the current draft will be replaced, with generation and review evidence retained.",
              rejectTitle: "Reject this intelligent design?",
              deleteTitle: "Delete this generation record?",
              deleteDescription:
                "Only this unapplied generation history record will be removed. The current scheme version and applied design stay unchanged.",
            },
            scenarios: {
              government_notice: "Government Notice",
              meeting_invitation: "Meeting Invitation",
              financial_approval: "Financial Approval",
              supply_chain_collaboration: "Supply Chain Collaboration",
              recruitment: "Recruitment",
              cloud_drive_share: "Cloud Drive Share",
              password_expiry: "Password Expiry",
              system_upgrade: "System Upgrade",
              customer_complaint: "Customer Complaint",
              delivery_notice: "Delivery Notice",
              project_delivery: "Project Delivery",
              urgent_security_notice: "Urgent Security Notice",
            },
            empty: "None",
          },
        },
      },
    },
  },
  ja: {
    redblue: {
      pages: {
        schemeDetail: {
          intelligence: {
            types: {
              attackChain: "攻撃チェーン演習",
              phishingSimulation: "制御フィッシング演習",
            },
            defaults: {
              attackChainIntent:
                "政府・企業の内部ネットワーク攻防演習向けに、多段階の制御された演習設計を生成してください。境界露出リスクを初期手掛かりとし、初期アクセス模擬、認証情報リスクの発見、横展開兆候の識別、ブルーチームの検知と対応、振り返り採点までをつなげます。すべての攻撃動作は人手レビュー対象の演習タスクと安全なプレースホルダーに限定し、実際の脆弱性悪用ペイロード、悪意あるスクリプト、C2、遠隔操作、認証情報収集、検知回避内容は出力しません。段階、タスク、検知点、採点ルール、レポートテンプレート、知識参照を含めてください。",
              phishingIntent:
                "政府・企業の従業員セキュリティ意識向上訓練向けに、制御されたインテリジェントフィッシング演習設計を生成してください。認可済みの模擬メールボックスまたは隔離アカウントを前提に、レビュー可能なメール件名、送信者ID、本文、行動誘導、リスク提示、検知指標、ブルーチーム対応タスク、採点ルール、振り返りレポートを作成します。内容は安全教育と隔離演習のみに使用し、実際の認証情報収集、悪意ある添付ファイル、検知回避、外部配送、ソーシャルエンジニアリング実行手順は出力しません。",
            },
            attackChainClasses: {
              social_engineering_identity_response: "ソーシャルエンジニアリングと ID 対応",
              exposure_initial_access_boundary_response: "露出面と境界防御対応",
              credential_lateral_movement_segmentation: "認証情報リスクと横展開遮断",
              cloud_native_workload_response: "クラウドネイティブワークロード対応",
              persistence_honeypot_attribution: "潜伏兆候、ハニーポット、追跡分析",
            },
            statuses: {
              pending: "待機中",
              running: "生成中",
              generated: "レビュー待ち",
              applied: "採用済み",
              rejected: "却下済み",
              failed: "失敗",
            },
            languageOptions: {
              zhCN: "简体中文",
              enUS: "English",
              jaJP: "日本語",
            },
            actions: {
              generate: "演習設計を生成",
              refresh: "更新",
              retry: "再試行",
              view: "詳細を表示",
              apply: "現在の版へ採用",
              reject: "却下",
            },
            sections: {
              generate: "インテリジェント生成",
              history: "生成とレビュー履歴",
              designSummary: "設計概要",
              stages: "演習段階",
              tasks: "演習タスク",
              reports: "レポートテンプレート",
              knowledge: "知識参照",
              phishingArtifact: "制御フィッシング成果物",
              rawOutput: "失敗時の生出力",
            },
            fields: {
              id: "記録 ID",
              type: "生成種別",
              attackChainClass: "攻撃チェーン分類",
              intent: "演習意図",
              scenario: "フィッシング場面",
              language: "言語",
              latency: "所要時間",
              reviewNote: "レビュー所見",
              stage: "段階",
              task: "タスク",
              scoreKind: "採点方式",
              maxScore: "最高点",
              reference: "参照",
              rationale: "参照理由",
              generationType: "生成種別",
              requestId: "インテリジェンス request_id",
              traceId: "トレース trace_id",
              provider: "プロバイダー",
              model: "モデル",
              promptVersion: "プロンプト版",
              reviewedBy: "レビュー者",
              reviewedAt: "レビュー日時",
              errorCode: "エラーコード",
              errorMessage: "エラー内容",
              summary: "概要",
              safety: "安全分類",
              targetRole: "対象ロール",
              sender: "模擬送信者",
              subject: "件名",
              callToAction: "模擬アクション",
              body: "本文",
              detectionIndicators: "検知ポイント",
            },
            placeholders: {
              attackChainClass: "今回の生成に対応する受入分類を選択",
              intent: "対象環境、訓練目的、想定段階、制約を記述",
              scenario: "制御演習の場面を選択",
              reviewNote: "採用または却下の根拠を記録",
            },
            validation: {
              attackChainClassRequired: "攻撃チェーン生成には分類が必要です。",
              intentRequired: "演習意図を入力してください。",
              scenarioRequired: "制御フィッシング演習では場面が必要です。",
            },
            notices: {
              providerTitle: "インテリジェンスセンターで生成し RedBlue でレビュー",
              providerDescription:
                "ローカルテンプレートによる成功偽装は行いません。攻撃チェーンとフィッシング内容は隔離された演習環境内の制御訓練に限定し、送信者アドレスには予約済み .invalid ドメインを使用します。外部送信、認証情報収集、悪意あるペイロードは提供しません。",
              emptyHistory: "この版にはまだ生成記録がありません。",
            },
            messages: {
              loadFailed: "生成記録の取得に失敗しました",
              generated: "レビュー対象の設計を取得しました",
              generateFailed: "インテリジェント生成に失敗しました",
              applied: "現在の下書き版へ採用しました",
              applyFailed: "採用に失敗しました",
              rejected: "生成記録を却下しました",
              rejectFailed: "却下に失敗しました",
              deleted: "生成記録を削除しました",
              deleteFailed: "生成記録の削除に失敗しました",
              contractMissing:
                "成功状態に必須の設計結果がありません。レビューと採用を停止しました。request_id、trace_id、元の応答からサーバー契約を診断してください。",
            },
            confirm: {
              applyTitle: "この設計を採用しますか？",
              applyDescription:
                "現在の下書き版の段階、タスク、採点ルール、レポートテンプレートを置き換え、生成とレビューの証跡を保持します。",
              rejectTitle: "この設計を却下しますか？",
              deleteTitle: "この生成記録を削除しますか？",
              deleteDescription:
                "未採用の生成履歴のみを削除します。現在のプラン版と採用済み設計は変更されません。",
            },
            scenarios: {
              government_notice: "行政通知",
              meeting_invitation: "会議招待",
              financial_approval: "財務承認",
              supply_chain_collaboration: "サプライチェーン連携",
              recruitment: "採用連絡",
              cloud_drive_share: "クラウド共有",
              password_expiry: "パスワード期限",
              system_upgrade: "システム更新",
              customer_complaint: "顧客苦情",
              delivery_notice: "配送通知",
              project_delivery: "プロジェクト納品",
              urgent_security_notice: "緊急セキュリティ通知",
            },
            empty: "なし",
          },
        },
      },
    },
  },
};
